import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSubmitSupportReportMutation } from '@/store/supportApi';
import { collectSupportContext } from '@/utils/supportContext';
import { useToast } from '@/hooks/useToast';
import {
  Backdrop, Dialog, Title, Field, RadioRow, Textarea,
  CheckboxRow, Preview, HelpText, Actions, CancelButton, SubmitButton,
} from './SupportDialog.styled';

export interface SupportDialogProps {
  open: boolean;
  onClose: () => void;
}

type ReportType = 'Bug' | 'Support';

export default function SupportDialog({ open, onClose }: SupportDialogProps) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const [submit, { isLoading }] = useSubmitSupportReportMutation();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [type, setType] = useState<ReportType>('Bug');
  const [description, setDescription] = useState('');
  const [attach, setAttach] = useState(false);
  const [screenshot, setScreenshot] = useState<string | undefined>(undefined);
  const [shotError, setShotError] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => dialogRef.current?.focus());
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  useEffect(() => {
    if (open) {
      setType('Bug');
      setDescription('');
      setAttach(false);
      setScreenshot(undefined);
      setShotError(false);
    }
  }, [open]);

  const captureScreenshot = useCallback(async () => {
    setShotError(false);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const node = dialogRef.current;
      if (node) node.style.visibility = 'hidden';
      const canvas = await html2canvas(document.body, { logging: false, useCORS: true });
      if (node) node.style.visibility = '';
      setScreenshot(canvas.toDataURL('image/png'));
    } catch {
      setShotError(true);
      setScreenshot(undefined);
    }
  }, []);

  const handleAttachToggle = useCallback((checked: boolean) => {
    setAttach(checked);
    if (checked) {
      void captureScreenshot();
    } else {
      setScreenshot(undefined);
      setShotError(false);
    }
  }, [captureScreenshot]);

  const handleSubmit = useCallback(async () => {
    try {
      await submit({
        type,
        description,
        screenshot: attach ? screenshot : undefined,
        ...collectSupportContext(),
      }).unwrap();
      showSuccess(t('support.success'));
      onClose();
    } catch {
      showError(t('support.error'));
    }
  }, [submit, type, description, attach, screenshot, showSuccess, showError, t, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-title"
        tabIndex={-1}
      >
        <Title id="support-title">{t('support.title')}</Title>

        <Field>
          <span>{t('support.typeLabel')}</span>
          <RadioRow>
            <label>
              <input
                type="radio"
                name="support-type"
                checked={type === 'Bug'}
                onChange={() => setType('Bug')}
              />{' '}
              {t('support.typeBug')}
            </label>
            <label>
              <input
                type="radio"
                name="support-type"
                checked={type === 'Support'}
                onChange={() => setType('Support')}
              />{' '}
              {t('support.typeSupport')}
            </label>
          </RadioRow>
        </Field>

        <Field>
          <label htmlFor="support-description">{t('support.descriptionLabel')}</label>
          <Textarea
            id="support-description"
            value={description}
            placeholder={t('support.descriptionPlaceholder')}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <Field>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={attach}
              onChange={(e) => handleAttachToggle(e.target.checked)}
            />
            {t('support.attachScreenshot')}
          </CheckboxRow>
          {shotError && <HelpText>{t('support.screenshotFailed')}</HelpText>}
          {attach && screenshot && (
            <Preview src={screenshot} alt={t('support.screenshotPreviewAlt')} />
          )}
        </Field>

        <Actions>
          <CancelButton type="button" onClick={onClose}>
            {t('support.cancel')}
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || description.trim().length === 0}
          >
            {t('support.submit')}
          </SubmitButton>
        </Actions>
      </Dialog>
    </>,
    document.body,
  );
}
